import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["infoIcons", "compactLayoutBreakpoint", "description", "descriptionLink", "icon", "id", "imageSource", "index", "hasPlaylistNumbering", "link", "menuItems", "priorityPill", "title", "subtitle", "modalSlot", "isDisabled", "isDimmed", "rank"];
import "core-js/modules/es.string.link.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.symbol.description.js";
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { pxToRem, alpha } from 'utils/ui';
import Card from 'components/Card';
import Box from 'components/Box';
import Flex from 'components/Flex';
import Text from 'components/Text';
import ViewMoreText from 'components/ViewMoreText';
import useBreakpoints from 'hooks/useBreakpoints';
import { ActionMenu, IconsAndMenuBox, CardTitle, CardLink, Image, StackableCardContent, Subtitle, getCardTitleId, getCardSubtitleId, getCardDescriptionId, getCardMenuId } from '../shared';
import { Link } from 'react-router-dom';
import TooltipButton from 'components/TooltipButton';
var HorizontalCardWrapper = styled(Card).withConfig({
  displayName: "HorizontalCard__HorizontalCardWrapper",
  componentId: "sc-okulhk-0"
})(["", ""], function (p) {
  return p.$isDimmed && "\n\t\tfilter: brightness(1.3) saturate(0.5);";
});
var HorizontalImageWrapper = styled.div.withConfig({
  displayName: "HorizontalCard__HorizontalImageWrapper",
  componentId: "sc-okulhk-1"
})(["flex:none;width:25%;max-width:", ";background-color:", ";border-radius:", ";border-top-right-radius:0;border-bottom-right-radius:0;overflow:hidden;"], pxToRem(220), function (p) {
  return alpha(p.theme.shade, p.theme.shadeStrength);
}, function (p) {
  return pxToRem(p.theme.globals.borderRadius.large);
});

/**
 * These styles will be applied to the ViewMoreText
 * component's button element, and are needed to
 * make it clickable on top of the Title's cover link.
 * Additionally, extra spacing is applied around the link
 * to make it easier to click.
 */

var ViewMoreTextWithRaisedButton = styled(ViewMoreText).withConfig({
  displayName: "HorizontalCard__ViewMoreTextWithRaisedButton",
  componentId: "sc-okulhk-2"
})(["position:relative;padding:", ";margin:-", ";z-index:", ";"], function (p) {
  return p.theme.globals.spacing.xs;
}, function (p) {
  return p.theme.globals.spacing.xs;
}, function (p) {
  return p.theme.globals.z.raised;
});
function Description(_ref) {
  var id = _ref.id,
    text = _ref.text,
    iconLink = _ref.iconLink;
  var _useBreakpoints = useBreakpoints(),
    isMediumScreen = _useBreakpoints.xxs;
  return /*#__PURE__*/React.createElement(Text, {
    id: id,
    size: ['s', 'm'],
    mt: ['s', 'm'],
    mb: ['xxs', 0],
    maxWidth: 640,
    breakpoints: "xs",
    as: "div",
    overflow: "wrap"
  }, /*#__PURE__*/React.createElement(ViewMoreTextWithRaisedButton, {
    content: text,
    maxLength: isMediumScreen ? 150 : 40
  }),
  // Description Link
  iconLink && /*#__PURE__*/React.createElement(React.Fragment, null, ' ', /*#__PURE__*/React.createElement(TooltipButton, {
    as: Link,
    to: iconLink.link,
    icon: iconLink.icon,
    iconOnly: true,
    z: "raised",
    position: "relative",
    size: "small",
    title: iconLink.title
  })));
}
var HorizontalCard = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var infoIcons = _ref2.infoIcons,
    _ref2$compactLayoutBr = _ref2.compactLayoutBreakpoint,
    compactLayoutBreakpoint = _ref2$compactLayoutBr === void 0 ? 'xs' : _ref2$compactLayoutBr,
    description = _ref2.description,
    descriptionLink = _ref2.descriptionLink,
    icon = _ref2.icon,
    id = _ref2.id,
    imageSource = _ref2.imageSource,
    index = _ref2.index,
    hasPlaylistNumbering = _ref2.hasPlaylistNumbering,
    link = _ref2.link,
    menuItems = _ref2.menuItems,
    priorityPill = _ref2.priorityPill,
    title = _ref2.title,
    subtitle = _ref2.subtitle,
    modalSlot = _ref2.modalSlot,
    isDisabled = _ref2.isDisabled,
    isDimmed = _ref2.isDimmed,
    rank = _ref2.rank,
    otherProps = _objectWithoutProperties(_ref2, _excluded);
  var titleId = getCardTitleId(id);
  var subtitleId = getCardSubtitleId(id);
  var descriptionId = getCardDescriptionId(id);
  var menuId = getCardMenuId(id);
  var cardLink = isDisabled ? undefined : link;
  return /*#__PURE__*/React.createElement(HorizontalCardWrapper, _extends({
    ref: ref,
    id: id,
    alternateBackground: true,
    variant: isDisabled ? 'default' : 'clickable',
    $isDimmed: isDimmed,
    display: "flex",
    overflow: "visible",
    forwardedAs: "article",
    "aria-labelledby": titleId
  }, otherProps), /*#__PURE__*/React.createElement(HorizontalImageWrapper, null, /*#__PURE__*/React.createElement(Image, {
    link: cardLink,
    icon: icon,
    aspectRatio: 2 / 3,
    src: imageSource,
    dimmed: isDimmed
  })), /*#__PURE__*/React.createElement(StackableCardContent, {
    stackBelow: compactLayoutBreakpoint,
    p: "s",
    pb: "xs"
  }, /*#__PURE__*/React.createElement(Box, {
    grow: true,
    shrink: true,
    dimmed: isDimmed
  }, !isDisabled && priorityPill && /*#__PURE__*/React.createElement(Box, {
    ml: "-xxs",
    mb: "xs",
    z: "raised",
    position: "relative"
  }, priorityPill), /*#__PURE__*/React.createElement(CardTitle, {
    id: titleId,
    mt: "-xxxs",
    mb: "xxs"
  }, /*#__PURE__*/React.createElement(CardLink, {
    link: cardLink,
    maxLength: 85,
    rank: hasPlaylistNumbering ? rank : undefined
  }, title)), /*#__PURE__*/React.createElement(Subtitle, {
    id: subtitleId
  }, subtitle), description && /*#__PURE__*/React.createElement(Description, {
    id: descriptionId,
    text: description,
    iconLink: descriptionLink
  })), /*#__PURE__*/React.createElement(IconsAndMenuBox, {
    stackedBelow: compactLayoutBreakpoint,
    breakpoints: compactLayoutBreakpoint,
    pl: "xs",
    mr: ['-xs', '-xxs']
  }, /*#__PURE__*/React.createElement(Flex, {
    align: "top"
  }, infoIcons, menuItems && /*#__PURE__*/React.createElement(ActionMenu, {
    items: menuItems,
    id: menuId,
    titleId: titleId
  })))), modalSlot);
});
HorizontalCard.displayName = 'HorizontalCard';
export default HorizontalCard;